import { Injectable } from '@angular/core';
import { IdbService } from '../../indexDB/idb.service';
import { RotationCheck, RotationCheckEntry } from 'src/app/models/RotationCheck';
import { RotationCheckPhoto } from 'src/app/models/RotationCheckPhoto';
import { IRotationCheckDataSource } from './rotation-check.datasource';

export const RC_SITE_STORE = 'rotationChecks';
export const RC_ENTRY_STORE = 'rotationCheckEntries';
export const RC_PHOTO_STORE = 'rotationCheckPhotos';

/**
 * IndexedDB-backed rotation-check storage — the on-device source of truth.
 *
 * Reads and writes are always local, so the module works with no connection.
 * RotationCheckSyncService handles the server side in the background: it pushes
 * locally-written records (those flagged syncStatus='pending') up to the API
 * and pulls server records back down into these stores.
 */
@Injectable({ providedIn: 'root' })
export class IdbRotationCheckDataSource implements IRotationCheckDataSource {
  constructor(private idb: IdbService) {}

  // ───── reads ──────────────────────────────────────────────────────────

  async listSites(): Promise<RotationCheck[]> {
    return ((await this.idb.list(RC_SITE_STORE)) as RotationCheck[]) || [];
  }

  async listEntries(): Promise<RotationCheckEntry[]> {
    return ((await this.idb.list(RC_ENTRY_STORE)) as RotationCheckEntry[]) || [];
  }

  async listEntriesForSite(siteId: string): Promise<RotationCheckEntry[]> {
    const all = await this.listEntries();
    return all.filter((e) => e.rotationCheckID === siteId);
  }

  /**
   * Read through the `rotationCheckEntryID` index rather than scanning the store.
   *
   * The previous full-store scan materialised every photo in the database — bytes
   * included — to return the few belonging to one entry, and the view page calls
   * this once per entry in parallel. Tolerable when a photo was a small base64
   * string; not once a record can carry a gigabyte video blob.
   */
  async listPhotosForEntry(entryId: string): Promise<RotationCheckPhoto[]> {
    return (
      ((await this.idb.getAllByIndex(
        RC_PHOTO_STORE,
        'rotationCheckEntryID',
        entryId
      )) as RotationCheckPhoto[]) || []
    );
  }

  async getSite(siteId: string): Promise<RotationCheck | null> {
    const record = await this.idb.select(RC_SITE_STORE, siteId);
    return record || null;
  }

  // ───── writes ─────────────────────────────────────────────────────────

  async putSite(site: RotationCheck): Promise<void> {
    await this.idb.put(RC_SITE_STORE, site);
  }

  async putEntry(entry: RotationCheckEntry): Promise<void> {
    await this.idb.put(RC_ENTRY_STORE, entry);
  }

  async putPhoto(photo: RotationCheckPhoto): Promise<void> {
    await this.idb.put(RC_PHOTO_STORE, photo);
  }

  async deleteSite(siteId: string): Promise<void> {
    await this.idb.simpleDelete(RC_SITE_STORE, siteId);
  }

  async deleteEntry(entryId: string): Promise<void> {
    await this.idb.simpleDelete(RC_ENTRY_STORE, entryId);
  }

  async deletePhoto(referenceCode: string): Promise<void> {
    await this.idb.simpleDelete(RC_PHOTO_STORE, referenceCode);
  }
}
